import { useState, useContext, useEffect } from 'react';
import {
  bool, string, func, any,
} from 'prop-types';
import {
  Button, Spin, Modal, Select,
} from 'antd';
import dynamic from 'next/dynamic';

import { i18nContext } from 'context/i18n';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { ProgressiveBlurredImg } from 'components/base';

import {
  getCookie, COOKIES, setCookie,
} from 'utils/helpers';

import { IS_CAMPER } from 'utils/constants/system';
import styles from './style.module.css';

const marketsWithMonthlyInstallments = IS_CAMPER ? [ 'TR', 'MX' ] : [];

const MembersOnlyModal = dynamic(() => import('../membersOnlyModal'));

const AddToBagBtn = ({
  onMouseOver, onMouseLeave, onClick, className, label,
  loading, market, t, setLoading, id, extraData,
  membersOnlyVisible, isMembersOnly, setModalMembersOnlyVisible, handleAddToCart, disabled = false,
}) => {
  const {
    locale, countries, appmode, bag, bagLength,
  } = useContext(i18nContext);
  const [ modalVisible, setModalVisible ] = useState(false);
  const [ hasBag, setHasbag ] = useState(false);
  const [ countryToSend, setCountryToSend ] = useState(null);
  useEffect(() => {
    if (appmode) {
      const bagHash = getCookie(COOKIES.BAG);
      const bagMarket = getCookie(COOKIES.BAG_MARKET);

      if (bagHash && bagMarket === market) {
        setHasbag(true);
      }
    }
  }, [ appmode, bagLength ]);

  const redirectToBag = () => {
    if (hasBag) {
      setLoading('A');
      window.location.assign(`${window.location.origin}/${locale}/bolsa`);
    }
  };

  useEffect(() => {
    if (countryToSend !== null) {
      const searchURL = new URLSearchParams(window.location.search);
      let countryPriceURL = searchURL.get('country_price');
      const [ ,, ...pathname ] = window.location.pathname.split('/');
      const countryToSendLanguages = countries.filter((item) => item.id === countryToSend)[0]?.languages;
      let defaultLanguage = 'en';

      if (countryToSendLanguages && countryToSendLanguages?.length > 0) {
        [ { id: defaultLanguage } ] = countryToSendLanguages.filter((item) => item.default === true);
      } else {
        const errorCountry = countries.filter((item) => item.id === countryToSend)[0];
        console.error('No hay idiomas para el país al que queremos enviar.', errorCountry);
      }

      if (countryPriceURL === null) {
        countryPriceURL = market;
      }

      const newPathname = `${defaultLanguage}_${countryToSend}/${pathname.join('/')}`;

      setCookie(COOKIES.COUNTRY_PRICE, countryPriceURL, 1, COOKIES.DOMAIN);

      setLoading('A');

      searchURL.set('country_price', countryPriceURL);
      searchURL.delete('country');
      searchURL.delete('lng');
      window.location.assign(`${window.location.origin}/${newPathname}?${searchURL.toString()}`);
    }
  }, [ countryToSend ]);

  const handleClick = async () => {
    await onClick();

    // const item = handleStorage('getItem', 'justAddedItem');
    if (bag && bag.length) {
      setHasbag(true);
    }
  };

  return (
    <>
      <Spin spinning={loading} id={id}>
        <Button
          block
          size="large"
          type="primary"
          disabled={disabled}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
          onClick={handleClick}
          className={className}
        >
          {label}
        </Button>
        {
          appmode && <>
            <Button
              block
              size="large"
              type="primary"
              disabled={disabled || !hasBag}
              onClick={redirectToBag}
              className={styles.goToBag}
            >
              {hasBag ? t('menu.superior', 'mi.bolsa') : t('menu.derecha', 'derecha.bolsa.vacia') }
            </Button>

            <div className={styles.countryToSendSelect}>
              <span>{t('compra.formulario', 'mi.cuenta.enviar.a', 'Ship to')}</span>
              <Select
                suffixIcon={<ChevronSVG />}
                placeholder={t('modal.idiomas', 'select.country', 'Select a country')}
                filterOption={false}
                onChange={(val) => {
                  setCountryToSend(val);
                }}
                value={countryToSend || market}
                className={`${styles.modalSelect} ${styles.noFloatLabel}`}
                virtual={false}
              >
                {extraData?.shippingCountries?.map((country) => (
                  <Select.Option key={country.id} className={styles.selectItem}>
                    <p>{country.name}</p>
                  </Select.Option>
                ))}
              </Select>
            </div>
          </>
        }
        {marketsWithMonthlyInstallments.includes(market) &&
          <>
            <button onClick={() => setModalVisible(true)} className={`${styles.extraInfoLabel} extraInfoButton`}>{t('ficha.producto', `pay.in.3.6.or.8.monthly.installments${market !== 'TR' ? `.${market.toLowerCase()}` : ''}`)}</button>
            <Modal
              title={t('ficha.producto', 'installments.titulo')}
              visible={modalVisible}
              footer={null}
              onCancel={() => setModalVisible(false)}
              wrapClassName={styles.modalWrapper}
              destroyOnClose={true}
              closeIcon={<CloseModalSVG />}
            >
              <div className={styles.modalContent}>
                <div className={styles.content}>
                  <p className={styles.description} dangerouslySetInnerHTML={{ __html: t('ficha.producto', `installments.description${market !== 'TR' ? `.${market.toLowerCase()}` : ''}`).replace(/(?:\r\n|\r|\n)/g, '<br />') }}></p>
                </div>
                <div className={styles.photo}>
                  <ProgressiveBlurredImg
                    highQualitySrc={'/assets-new/zapato_intallment.png'}
                    alt={'image of installment'}
                    width={151}
                    height={409}
                  />
                </div>
              </div>
            </Modal>
          </>
        }
        {isMembersOnly &&
          <MembersOnlyModal
            loading={loading}
            membersOnlyVisible={membersOnlyVisible}
            setModalMembersOnlyVisible={setModalMembersOnlyVisible}
            onFinishOk={handleAddToCart}
          />
        }
      </Spin>
    </>
  );
};

AddToBagBtn.defaultProps = {
  onMouseOver: () => true,
  onMouseLeave: () => true,
  onClick: () => true,
  setLoading: () => true,
  t: (...args) => args.join('.'),
  className: '',
  label: 'ADD TO BAG',
  loading: false,
  market: 'ES',
  id: null,
  isMembersOnly: true,
  membersOnlyVisible: false,
  setModalMembersOnlyVisible: () => true,
  handleAddToCart: () => true,
};

AddToBagBtn.propTypes = {
  onMouseOver: func,
  onMouseLeave: func,
  onClick: func,
  t: func,
  className: any,
  label: string,
  loading: bool,
  market: string,
  setLoading: func,
  extraData: any,
  id: string,
  isMembersOnly: bool,
  membersOnlyVisible: bool,
  setModalMembersOnlyVisible: func,
  handleAddToCart: func,
  disabled: bool,
};

export default AddToBagBtn;
